import React, {useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {ResponsiveBar} from "@nivo/bar";
import {ReportTotalCards} from "./ReportSharedLayout";
import {KPI_CHART_COLORS} from "../../common/ChartColors";

const metricDefinitions = {
  COUNT_BY_ADVERTISE: {field: 'countByAdvertise', label: '광고주 수', color: '#5470c6'},
  TOTAL_EXPOSURE_COUNT: {field: 'totalExposureCount', label: '총 노출수', color: KPI_CHART_COLORS.exposureCount},
  EXPOSURE_COUNT: {field: 'exposureCount', label: '노출수', color: KPI_CHART_COLORS.exposureCount},
  TOTAL_CLICK_COUNT: {field: 'totalClickCount', label: '총 클릭수', color: '#5b8ff9'},
  VALID_CLICK_COUNT: {field: 'validClickCount', label: '클릭수', color: '#61d9a5'},
  CLICK_RATE: {field: 'clickRate', label: '클릭률', color: '#65789b', suffix: '%'},
  COST_AMOUNT: {field: 'costAmount', label: '비용', color: KPI_CHART_COLORS.costAmount, suffix: '원'},
  CPC: {field: 'cpc', label: 'CPC', color: '#f6bd16', suffix: '원'},
  CONVERSION_PRICE: {field: 'conversionPrice', label: '전환단가', color: '#e8684a', suffix: '원'},
  CONVERSION_COUNT: {field: 'conversionCount', label: '전환수', color: '#6dc8ec'},
  CONVERSION_RATE: {field: 'conversionRate', label: '전환율', color: '#9270ca', suffix: '%'},
  AMOUNT_PURCHASED_AVG: {field: 'amountPurchasedAvg', label: '평균구매액', color: '#ff9d4d', suffix: '원'},
  SESSION_CONVERSION_AMOUNT: {field: 'sessionConversionAmount', label: '세션 매출', color: '#269a99', suffix: '원'},
  SESSION_CONVERSION_ROAS: {field: 'sessionConversionRoas', label: '세션 ROAS', color: '#7c5ce5', suffix: '%'},
  DIRECT_CONVERSION_AMOUNT: {field: 'directConversionAmount', label: '직접 매출', color: '#8e77ed', suffix: '원'},
  DIRECT_CONVERSION_ROAS: {field: 'directConversionRoas', label: '직접 ROAS', color: '#5d7092', suffix: '%'},
  ROAS: {field: 'roas', label: '총 ROAS', color: '#6f5ef9', suffix: '%'},
  E_CPM: {field: 'eCpm', label: 'CPV', color: KPI_CHART_COLORS.cpv, suffix: '원'}
};

const scopeDefinitions = {
  BY_ADVERTISE: {field: 'adverName', label: '광고주'},
  BY_CAMPAIGN: {field: 'campaignName', label: '캠페인'},
  BY_PRODUCT: {field: 'productType', label: '광고 상품'},
  BY_TARGETING: {field: 'targetingType', label: '타겟팅'}
};

const numberValue = value => {
  const parsed = Number(typeof value === 'string' ? value.replace(/,/g, '') : value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const metricValue = (row, id, definition) => {
  if (!definition) return 0;
  const direct = numberValue(row?.[definition.field]);
  if (direct) return direct;
  if (id === 'CLICK_RATE') return numberValue(row?.exposureCount) ? numberValue(row?.validClickCount) / numberValue(row?.exposureCount) * 100 : 0;
  if (id === 'CPC') return numberValue(row?.validClickCount) ? numberValue(row?.costAmount) / numberValue(row?.validClickCount) : 0;
  if (id === 'E_CPM') return numberValue(row?.exposureCount) ? numberValue(row?.costAmount) / numberValue(row?.exposureCount) * 1000 : 0;
  if (id === 'CONVERSION_RATE') return numberValue(row?.totalClickCount) ? numberValue(row?.conversionCount) / numberValue(row?.totalClickCount) * 100 : 0;
  return direct;
};

const periodLabel = (row, period) => {
  if (period === 'BY_DAILY') return row?.statisticsDate || row?.statisticsStartDate;
  if (period === 'BY_WEEKLY') return `${row?.statisticsStartDate || ''} 주`;
  if (period === 'BY_MONTHLY') return String(row?.statisticsDate || row?.statisticsStartDate || '').slice(0, 7);
  return '';
};

export default function CustomReportChart({data = [], setting = {}}) {
  const metricIds = useMemo(() => (Array.isArray(setting?.columns) ? setting.columns : [])
    .filter(id => metricDefinitions[id]), [setting?.columns]);
  const [visibleMetrics, setVisibleMetrics] = useState({});

  useEffect(() => {
    setVisibleMetrics(metricIds.reduce((result, id) => ({...result, [id]: true}), {}));
  }, [metricIds.join('|')]);

  const scopeIds = (Array.isArray(setting?.groupByScopes) ? setting.groupByScopes : [])
    .filter(id => scopeDefinitions[id]);
  const groupDescription = [setting?.groupByPeriod !== 'NONE' ? '기간' : null, ...scopeIds.map(id => scopeDefinitions[id].label)].filter(Boolean).join(' · ') || '전체';
  const rows = useMemo(() => (Array.isArray(data) ? data : []).map((row, index) => {
    const labels = [periodLabel(row, setting?.groupByPeriod), ...scopeIds.map(id => row?.[scopeDefinitions[id].field])].filter(Boolean);
    return {row, label: labels.join(' · ') || `데이터 ${index + 1}`};
  }), [data, setting?.groupByPeriod, scopeIds.join('|')]);
  const activeIds = metricIds.filter(id => visibleMetrics[id]);
  const primaryMetricId = activeIds[0] || metricIds[0];
  const maxima = activeIds.reduce((result, id) => ({...result, [id]: Math.max(...rows.map(({row}) => metricValue(row, id, metricDefinitions[id])), 1)}), {});
  const isPeriodChart = setting?.groupByPeriod && setting.groupByPeriod !== 'NONE';
  const periodChartData = rows.map(({row, label}) => activeIds.reduce((result, id) => ({
    ...result,
    [id]: metricValue(row, id, metricDefinitions[id])
  }), {groupLabel: label}));
  const rankedScopeRows = [...rows]
    .sort((first, second) => metricValue(second.row, primaryMetricId, metricDefinitions[primaryMetricId]) - metricValue(first.row, primaryMetricId, metricDefinitions[primaryMetricId]))
    .slice(0, 5);
  const totalExposure = data.reduce((sum, row) => sum + numberValue(row?.exposureCount ?? row?.totalExposureCount), 0);
  const totalCost = data.reduce((sum, row) => sum + numberValue(row?.costAmount), 0);
  const showTotals = metricIds.some(id => ['EXPOSURE_COUNT', 'TOTAL_EXPOSURE_COUNT', 'COST_AMOUNT', 'E_CPM'].includes(id));

  const toggleMetric = id => setVisibleMetrics(current => {
    if (current[id] && activeIds.length === 1) return current;
    return {...current, [id]: !current[id]};
  });

  return <ChartCard>
    <ChartHeader><div><ChartTitle>커스텀 보고서 주요 성과</ChartTitle><ChartDescription>{groupDescription} 기준 · 설정된 데이터 항목만 표시합니다.</ChartDescription></div>
      {showTotals && <ReportTotalCards exposureCount={totalExposure} costAmount={totalCost} compact/>}
    </ChartHeader>
    <MetricOptions>{metricIds.map(id => <MetricButton key={id} type="button" $active={visibleMetrics[id]} onClick={() => toggleMetric(id)}><MetricDot $active={visibleMetrics[id]} $color={metricDefinitions[id].color}/>{metricDefinitions[id].label}</MetricButton>)}</MetricOptions>
    {rows.length === 0 || metricIds.length === 0 ? <EmptyChart>차트로 표시할 데이터 항목이 없습니다.</EmptyChart> : isPeriodChart ? <PeriodChartArea>
      <ResponsiveBar
        data={periodChartData}
        keys={activeIds}
        indexBy="groupLabel"
        margin={{top:20,right:30,bottom:55,left:70}}
        padding={0.3}
        groupMode="grouped"
        valueScale={{type:'linear'}}
        indexScale={{type:'band',round:true}}
        colors={({id}) => metricDefinitions[id]?.color || '#94a3b8'}
        axisTop={null}
        axisRight={null}
        axisBottom={{tickSize:5,tickPadding:5,tickRotation:-45}}
        axisLeft={{tickSize:5,tickPadding:5,tickRotation:0,format:value => numberValue(value).toLocaleString('ko-KR')}}
        enableLabel={false}
        tooltip={({id,value,color,data:chartRow}) => <ChartTooltip><strong>{chartRow.groupLabel}</strong><span style={{color}}>{metricDefinitions[id]?.label}</span><b>{numberValue(value).toLocaleString('ko-KR')}{metricDefinitions[id]?.suffix || ''}</b></ChartTooltip>}
        legends={[]}
        role="application"
      />
    </PeriodChartArea> : <><TopFiveGuide>설정된 첫 번째 지표 기준 TOP 5</TopFiveGuide><ChartBody>{rankedScopeRows.map(({row, label}, index) => <ChartRow key={`${label}-${index}`}>
      <ChartLabel title={label}>{label}</ChartLabel><Bars>{activeIds.map(id => {
        const definition = metricDefinitions[id];
        const value = metricValue(row, id, definition);
        return <BarLine key={id}><BarName>{definition.label}</BarName><Track><Bar $color={definition.color} $width={value / maxima[id] * 100}/></Track><Value>{numberValue(value).toLocaleString('ko-KR')}{definition.suffix || ''}</Value></BarLine>;
      })}</Bars>
    </ChartRow>)}</ChartBody></>}
  </ChartCard>;
}

const ChartCard = styled.section`overflow:hidden;border:1px solid #e5e8eb;border-radius:10px;background:#fff;box-shadow:0 5px 18px rgba(31,41,55,.045);`;
const ChartHeader = styled.header`
  display:flex;
  min-height:64px;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  padding:15px 20px;
  border-bottom:1px solid #e8ebee;
  background:#fff;

  @media(max-width:900px){
    align-items:flex-start;
    flex-direction:column;
    width:100%;
  }
`;
const ChartTitle = styled.h3`margin:0 0 5px;color:#2f363d;font-size:14px;font-weight:800;`;
const ChartDescription = styled.p`margin:0;color:#8a9299;font-size:10px;`;
const MetricOptions = styled.div`display:flex;align-items:center;flex-wrap:wrap;justify-content:flex-start;gap:6px;padding:14px 20px 2px;`;
const MetricButton = styled.button`display:inline-flex;height:30px;align-items:center;gap:7px;padding:0 8px;border:0;border-radius:5px;background:transparent;color:#444;font-size:11px;font-weight:600;cursor:pointer;opacity:${({$active})=>$active?1:.4};transition:background-color .15s ease,opacity .15s ease;&:hover{background:#f7f8f9;opacity:1;}&:focus-visible{outline:1px solid #f5811f;outline-offset:2px;}`;
const MetricDot = styled.span`display:inline-block;width:8px;height:8px;flex:0 0 8px;box-sizing:border-box;border:2px solid ${({$color})=>$color};border-radius:2px;background:${({$active,$color})=>$active?$color:'#fff'};`;
const PeriodChartArea = styled.div`height:320px;padding:4px 20px 18px;`;
const TopFiveGuide = styled.div`padding:15px 20px 0;color:#8a9299;font-size:9px;font-weight:700;text-align:right;`;
const ChartBody = styled.div`display:flex;max-height:520px;flex-direction:column;gap:14px;padding:8px 20px 18px;overflow-y:auto;`;
const ChartRow = styled.div`display:grid;grid-template-columns:minmax(130px,22%) minmax(0,1fr);align-items:center;gap:14px;`;
const ChartLabel = styled.strong`overflow:hidden;color:#4b535b;font-size:10px;text-overflow:ellipsis;white-space:nowrap;`;
const Bars = styled.div`display:flex;min-width:0;flex-direction:column;gap:6px;`;
const BarLine = styled.div`display:grid;grid-template-columns:74px minmax(0,1fr) 90px;align-items:center;gap:7px;`;
const BarName = styled.span`overflow:hidden;color:#91989f;font-size:9px;text-overflow:ellipsis;white-space:nowrap;`;
const Track = styled.span`display:block;width:100%;height:7px;overflow:hidden;border-radius:999px;background:#f0f2f4;`;
const Bar = styled.span`display:block;width:${({$width})=>`${Math.max(2,Number($width)||0)}%`};height:100%;border-radius:inherit;background:${({$color})=>$color};`;
const Value = styled.span`color:#555e66;font-size:9px;font-weight:700;text-align:right;white-space:nowrap;`;
const EmptyChart = styled.div`display:flex;min-height:180px;align-items:center;justify-content:center;color:#969da4;font-size:11px;`;
const ChartTooltip = styled.div`display:flex;min-width:130px;flex-direction:column;gap:5px;padding:10px 12px;border:1px solid #e5e8eb;border-radius:7px;background:#fff;box-shadow:0 7px 20px rgba(31,41,55,.12);font-size:10px;strong{color:#454c53;}span{font-weight:700;}b{color:#555;font-size:11px;}`;
